import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "@mavenmm/teamwork-auth";

/**
 * Hook to fetch auth token for AI Assistant WebSocket
 * Exchanges Maven JWT for an ai-looker-upper compatible token
 * Uses React Query for caching across components
 */
export function useAssistantAuth(options?: { enabled?: boolean }) {
  const { getAccessToken, isAuthenticated } = useAuthContext();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ["assistantAuth"],
    queryFn: async () => {
      const mavenToken = getAccessToken();
      if (!mavenToken) {
        throw new Error("No access token available");
      }

      const response = await fetch("/.netlify/functions/getAssistantAuth", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mavenToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get auth token");
      }

      const data = await response.json();
      return data.authToken as string;
    },
    enabled: isAuthenticated && enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes - token is long-lived
    retry: 1,
  });

  return {
    authToken: query.data ?? null,
    authError: query.error?.message ?? null,
    isLoading: query.isLoading,
    refetchAuth: query.refetch,
  };
}
